import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Check, Search, PackageCheck, User } from "lucide-react";
import { api, apiError } from "@/lib/api";

const YMD_TODAY = () => new Date().toISOString().slice(0, 10);

/**
 * Modal "Concluir instalação".
 * Pesquisa vendas pendentes (não instaladas, não canceladas) e permite marcar como instaladas.
 */
export default function ConcluirInstalacaoModal({ onClose }) {
    const [vendas, setVendas] = useState([]);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [dataI, setDataI] = useState(YMD_TODAY());
    const [obs, setObs] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/vendas")
            .then((r) => {
                const list = (Array.isArray(r.data) ? r.data : [])
                    .filter((v) => !v.instalado && !v.cancelada);
                setVendas(list);
            })
            .catch(() => setVendas([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (q.length < 2) return vendas.slice(0, 20);
        const s = q.toLowerCase().trim();
        return vendas.filter((v) =>
            (v.nome || "").toLowerCase().includes(s) ||
            String(v.telefone || "").includes(s) ||
            String(v.nif || "").includes(s) ||
            (v.localidade || v.cidade || "").toLowerCase().includes(s)
        ).slice(0, 20);
    }, [vendas, q]);

    const concluir = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await api.put(`/vendas/${selected.id}`, {
                ...selected,
                instalado: true,
                dataInstalacao: dataI,
                observacoes: obs ? `${selected.observacoes || ""}\n[${dataI}] ${obs}`.trim() : selected.observacoes,
            });
            toast.success("Instalação concluída — a sincronizar com Kommo…", {
                description: `${selected.nome} · ${dataI}`,
            });
            onClose();
        } catch (e) {
            toast.error(apiError(e) || "Falha ao concluir");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} onClick={onClose}>
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", damping: 25, stiffness: 260 }}
                className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                style={{ background: "var(--v2-surface)", borderColor: "var(--v2-border)", color: "var(--v2-foreground)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="modal-concluir"
            >
                <div className="h-12 px-4 flex items-center border-b" style={{ borderColor: "var(--v2-border)" }}>
                    <PackageCheck size={15} style={{ color: "var(--v2-success)" }} />
                    <span className="ml-2 font-headings font-semibold">Concluir instalação</span>
                    <button onClick={onClose} className="ml-auto h-7 w-7 rounded-md flex items-center justify-center hover:bg-[color:var(--v2-surface-hover)]"><X size={15} /></button>
                </div>

                {!selected ? (
                    <>
                        <div className="p-3 border-b" style={{ borderColor: "var(--v2-border)" }}>
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-2.5" style={{ color: "var(--v2-muted)" }} />
                                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar por nome, telefone, NIF, cidade…"
                                    className="w-full h-8 pl-8 pr-2 rounded-md border bg-transparent text-[13px] outline-none focus:border-[color:var(--v2-primary)]"
                                    style={{ borderColor: "var(--v2-border)" }}
                                    data-testid="concluir-search"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? <div className="p-4 text-[12px]" style={{ color: "var(--v2-muted)" }}>A carregar…</div> :
                                filtered.length === 0 ? <div className="p-8 text-center text-[12px]" style={{ color: "var(--v2-muted)" }}>Nenhuma instalação pendente.</div> :
                                    filtered.map((v) => (
                                        <button key={v.id} onClick={() => setSelected(v)} className="w-full flex items-center gap-3 h-12 px-3 hover:bg-[color:var(--v2-surface-hover)] text-left border-b" style={{ borderColor: "var(--v2-border)" }} data-testid={`concluir-row-${v.id}`}>
                                            <User size={14} style={{ color: "var(--v2-muted)" }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13px] truncate">{v.nome}</div>
                                                <div className="text-[11px] truncate" style={{ color: "var(--v2-muted)" }}>{v.telefone} · {v.localidade || v.cidade || "—"}</div>
                                            </div>
                                            <span className="text-[10px] tabular-nums" style={{ color: "var(--v2-muted)" }}>{(v.dataVenda || "").slice(5)}</span>
                                        </button>
                                    ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="p-4 space-y-4 overflow-y-auto flex-1">
                            <div className="p-3 rounded-md border" style={{ background: "var(--v2-surface-hover)", borderColor: "var(--v2-border)" }}>
                                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--v2-muted)" }}>Cliente</div>
                                <div className="text-[14px] font-semibold">{selected.nome}</div>
                                <div className="text-[11px]" style={{ color: "var(--v2-muted)" }}>{selected.telefone} · {selected.localidade || selected.cidade || "—"}</div>
                            </div>
                            <label className="block">
                                <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--v2-muted)" }}>Data efectiva</span>
                                <input type="date" value={dataI} onChange={(e) => setDataI(e.target.value)} className="mt-1 w-full h-9 px-3 rounded-md border bg-transparent text-[13px]" style={{ borderColor: "var(--v2-border)" }} data-testid="concluir-data" />
                            </label>
                            <label className="block">
                                <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--v2-muted)" }}>Observações (opcional)</span>
                                <textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex: cliente satisfeito, router entregue…" className="mt-1 w-full px-3 py-2 rounded-md border bg-transparent text-[13px] resize-none" style={{ borderColor: "var(--v2-border)" }} data-testid="concluir-obs" />
                            </label>
                        </div>
                        <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--v2-border)" }}>
                            <button onClick={() => setSelected(null)} className="flex-1 h-10 rounded-md border text-[13px] font-medium" style={{ borderColor: "var(--v2-border)" }}>Voltar</button>
                            <button onClick={concluir} disabled={saving} className="flex-1 h-10 rounded-md text-white text-[13px] font-bold flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "var(--v2-success)" }} data-testid="concluir-confirm">
                                <Check size={14} /> {saving ? "A concluir…" : "Confirmar"}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
